import { ExternalLinkIcon, MessageCircleIcon, PlusIcon } from 'lucide-react'
import { useState } from 'react'
import { Boton } from '@/components/ui/Boton'
import { ChipEstado } from '@/components/ui/ChipEstado'
import { TextoConFormato } from '@/components/ui/TextoConFormato'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { Confirmacion } from '@/features/admin/Confirmacion'
import { formatearPeriodo } from '@/features/grilla/formato'
import { cn } from '@/lib/cn'
import type { PlanificacionResumen } from '@/lib/schemas/planificacion'
import { ModalPlanificacion } from './ModalPlanificacion'
import { PanelPlanificacion } from './PanelPlanificacion'
import { useAccionPlanificacion, usePlanificaciones } from './api'

interface VistaPlanificacionProps {
  proyectoId: string
  esRastro: boolean
  /** Carpeta de Drive del cliente, para el selector del documento (Fase 6). */
  carpetaDrive?: string | null
}

/**
 * P05 · pestaña Planificación (Fase 5a). Una tarjeta ancha por período, la
 * más reciente arriba. La tarjeta no tiene menú: tocarla abre el panel (C10),
 * como en la grilla. "Abrir documento" va directo a Google Docs.
 *
 * Sin contador ni "Empezar revisión" (decisión de Serena, 21/09): el estado
 * se ve en el chip de cada tarjeta.
 */
export function VistaPlanificacion({
  proyectoId,
  esRastro,
  carpetaDrive = null,
}: VistaPlanificacionProps) {
  const avisar = useAvisar()
  const [verBaja, setVerBaja] = useState(false)
  const lista = usePlanificaciones(proyectoId, esRastro && verBaja)
  const accion = useAccionPlanificacion()
  const [abierta, setAbierta] = useState<string | null>(null)
  const [editando, setEditando] = useState<PlanificacionResumen | null>(null)
  const [nueva, setNueva] = useState(false)
  const [dandoDeBaja, setDandoDeBaja] = useState<PlanificacionResumen | null>(null)

  const planificaciones = lista.data?.planificaciones ?? []

  return (
    <>
      {esRastro && (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          <Boton
            variante="sutil"
            tamano="chico"
            aria-pressed={verBaja}
            data-mostrar-baja-plan
            onClick={() => {
              setVerBaja((x) => !x)
            }}
          >
            {verBaja ? 'Ocultar dadas de baja' : 'Mostrar dadas de baja'}
          </Boton>
          <Boton
            tamano="chico"
            className="hidden sm:inline-flex"
            onClick={() => {
              setNueva(true)
            }}
          >
            <PlusIcon className="size-4" aria-hidden />
            Nueva planificación
          </Boton>
        </div>
      )}

      {lista.isPending ? (
        <p className="py-10 text-center text-sm text-tinta-suave">Cargando…</p>
      ) : planificaciones.length === 0 ? (
        <section className="rounded-tarjeta bg-tarjeta px-6 py-12 text-center shadow-tarjeta">
          <h2 className="text-lg font-semibold">
            {esRastro
              ? 'Este proyecto no tiene planificaciones.'
              : 'Todavía no hay planificaciones.'}
          </h2>
          <p className="mt-1 text-sm text-tinta-suave">
            {esRastro ? 'Creá la primera.' : 'RASTRO está preparando la primera.'}
          </p>
          {esRastro && (
            <Boton
              className="mt-4"
              onClick={() => {
                setNueva(true)
              }}
            >
              <PlusIcon className="size-4" aria-hidden />
              Nueva planificación
            </Boton>
          )}
        </section>
      ) : (
        <ul className="flex flex-col gap-3">
          {planificaciones.map((p) => (
            <li key={p.id}>
              <TarjetaPlanificacion
                planificacion={p}
                esRastro={esRastro}
                alAbrir={() => {
                  setAbierta(p.id)
                }}
              />
            </li>
          ))}
        </ul>
      )}

      {/* Botón flotante de celular, encima de la barra de pestañas. */}
      {esRastro && (
        <div className="fixed right-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-30 sm:hidden">
          <Boton
            tamano="icono"
            className="size-14 shadow-tarjeta"
            aria-label="Nueva planificación"
            onClick={() => {
              setNueva(true)
            }}
          >
            <PlusIcon className="size-6" aria-hidden />
          </Boton>
        </div>
      )}

      {abierta && (
        <PanelPlanificacion
          planificacionId={abierta}
          esRastro={esRastro}
          alCerrar={() => {
            setAbierta(null)
          }}
          alEditar={(p) => {
            setAbierta(null)
            setEditando(p)
          }}
          alDarDeBaja={(p) => {
            setAbierta(null)
            setDandoDeBaja(p)
          }}
        />
      )}

      <ModalPlanificacion
        abierto={nueva || editando !== null}
        alCerrar={() => {
          setNueva(false)
          setEditando(null)
        }}
        proyectoId={proyectoId}
        planificacion={editando}
        proximoDesde={lista.data?.proximoDesde ?? ''}
        proximoHasta={lista.data?.proximoHasta ?? ''}
        carpetaDrive={carpetaDrive}
      />

      <Confirmacion
        abierto={dandoDeBaja !== null}
        alCerrar={() => {
          setDandoDeBaja(null)
        }}
        titulo="Dar de baja la planificación"
        texto='La planificación deja de verse para el cliente. Podés restaurarla con "Mostrar dadas de baja".'
        confirmar="Dar de baja"
        peligro
        alConfirmar={async () => {
          if (!dandoDeBaja) return
          await accion.mutateAsync({ id: dandoDeBaja.id, accion: 'baja' })
          avisar('Planificación dada de baja')
        }}
      />
    </>
  )
}

function TarjetaPlanificacion({
  planificacion: p,
  esRastro,
  alAbrir,
}: {
  planificacion: PlanificacionResumen
  esRastro: boolean
  alAbrir: () => void
}) {
  return (
    <article
      data-planificacion={p.titulo}
      className={cn(
        // Se levanta al pasar el mouse, como las tarjetas de la grilla (DEC-23).
        'al-pasar relative flex flex-col gap-2 rounded-tarjeta bg-tarjeta p-4 shadow-tarjeta sm:p-5',
        p.dadaDeBaja && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">
            {/* El botón cubre la tarjeta entera; "Abrir documento" queda encima. */}
            <button
              type="button"
              onClick={alAbrir}
              className="text-left after:absolute after:inset-0 after:rounded-tarjeta focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-tinta"
            >
              {p.titulo}
            </button>
          </h2>
          <p className="text-sm text-tinta-suave">
            {formatearPeriodo(p.fechaDesde, p.fechaHasta)}
            {p.dadaDeBaja && ' · dada de baja'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {p.nuevo && (
            <span
              data-nuevo
              className="size-2.5 rounded-full bg-nuevo"
              aria-label="Novedad desde tu última visita"
              role="img"
            />
          )}
          <ChipEstado estado={p.estado} paraRastro={esRastro} />
        </div>
      </div>

      {p.descripcion && (
        <TextoConFormato texto={p.descripcion} lineas={2} className="text-tinta-suave" />
      )}

      <div className="flex flex-wrap items-center gap-4 pt-1">
        <a
          href={p.urlDocumento}
          target="_blank"
          rel="noopener noreferrer"
          className="relative z-10 inline-flex items-center gap-1.5 text-sm font-medium underline-offset-2 hover:underline"
        >
          <ExternalLinkIcon className="size-4" aria-hidden />
          Abrir documento
        </a>
        <span
          className="inline-flex items-center gap-1.5 text-sm text-tinta-suave"
          aria-label={`${String(p.comentarios)} comentarios`}
        >
          <MessageCircleIcon className="size-4" aria-hidden />
          {p.comentarios}
        </span>
      </div>
    </article>
  )
}
